import React, {useState, useRef, useCallback, useEffect} from 'react';

const PROVINCE_MAP: Record<string, string> = {
  'agrigento':'AG','alessandria':'AL','ancona':'AN','aosta':'AO',"valle d'aosta":'AO',
  "l'aquila":'AQ','aquila':'AQ','arezzo':'AR','ascoli piceno':'AP','asti':'AT',
  'avellino':'AV','bari':'BA','barletta-andria-trani':'BT','belluno':'BL',
  'benevento':'BN','bergamo':'BG','biella':'BI','bologna':'BO','bolzano':'BZ',
  'brescia':'BS','brindisi':'BR','cagliari':'CA','caltanissetta':'CL',
  'campobasso':'CB','caserta':'CE','catania':'CT','catanzaro':'CZ','chieti':'CH',
  'como':'CO','cosenza':'CS','cremona':'CR','crotone':'KR','cuneo':'CN',
  'enna':'EN','fermo':'FM','ferrara':'FE','firenze':'FI','foggia':'FG',
  'forlì-cesena':'FC','frosinone':'FR','genova':'GE','gorizia':'GO',
  'grosseto':'GR','imperia':'IM','isernia':'IS','la spezia':'SP','latina':'LT',
  'lecce':'LE','lecco':'LC','livorno':'LI','lodi':'LO','lucca':'LU',
  'macerata':'MC','mantova':'MN','massa-carrara':'MS','matera':'MT',
  'messina':'ME','milano':'MI','modena':'MO','monza e brianza':'MB','monza':'MB',
  'napoli':'NA','novara':'NO','nuoro':'NU','ogliastra':'OG','olbia-tempio':'OT',
  'oristano':'OR','padova':'PD','palermo':'PA','parma':'PR','pavia':'PV',
  'perugia':'PG','pesaro e urbino':'PU','pescara':'PE','piacenza':'PC',
  'pisa':'PI','pistoia':'PT','pordenone':'PN','potenza':'PZ','prato':'PO',
  'ragusa':'RG','ravenna':'RA','reggio calabria':'RC','reggio emilia':'RE',
  'rieti':'RI','rimini':'RN','roma':'RM','roma capitale':'RM','rovigo':'RO',
  'salerno':'SA','sassari':'SS','savona':'SV','siena':'SI','siracusa':'SR',
  'sondrio':'SO','taranto':'TA','teramo':'TE','terni':'TR','torino':'TO',
  'trapani':'TP','trento':'TN','treviso':'TV','trieste':'TS','udine':'UD',
  'varese':'VA','venezia':'VE','verbano-cusio-ossola':'VB','vercelli':'VC',
  'verona':'VR','vibo valentia':'VV','vicenza':'VI','viterbo':'VT',
};

export interface AddressParsed {
  address: string;
  city: string;
  zip: string;
  province: string;
}

function parseProvince(county: string): string {
  if (!county) return '';
  const normalized = county.split('/').pop()!.toLowerCase()
    .replace(/città metropolitana di /g, '')
    .replace(/provincia di /g, '')
    .trim();
  return PROVINCE_MAP[normalized] || '';
}

function cleanName(raw: string): string {
  if (!raw) return '';
  return raw.split('/').pop()!.trim();
}

function parseFeature(feature: any): AddressParsed {
  const p = feature.properties;
  const address  = p.street || p.name || '';
  const city     = cleanName(p.city || p.town || p.village || p.municipality || '');
  const zip      = p.postcode || '';
  const province = parseProvince(p.county || '');
  return {address, city, zip, province};
}

function featureLabel(feature: any): string {
  const p = feature.properties;
  const street = p.street || p.name || '';
  const city   = cleanName(p.city || p.town || p.village || '');
  const zip    = p.postcode || '';
  if (street) return `${street}${zip ? ', ' + zip : ''}${city ? ' ' + city : ''}`;
  return city;
}

function splitStreetNumber(text: string) {
  const m = text.match(/^(.*?)(\s+\d+\w*)$/);
  return m ? {street: m[1].trim(), number: m[2].trim()} : {street: text.trim(), number: ''};
}

interface Props {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  onSelect?: (parsed: AddressParsed) => void;
  placeholder?: string;
  className?: string;
}

export default function AddressAutocomplete({label, value, onChange, onSelect, placeholder, className}: Props) {
  const initial = splitStreetNumber(value || '');
  const [street, setStreet]     = useState(initial.street);
  const [civicNum, setCivicNum] = useState(initial.number);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [loading, setLoading]   = useState(false);
  const [open, setOpen]         = useState(false);
  const timer   = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  const doFetch = async (q: string) => {
    setLoading(true);
    try {
      const res  = await fetch(`https://photon.komoot.io/api/?q=${encodeURIComponent(q)}&limit=6`);
      const data = await res.json();
      setSuggestions(data.features || []);
      setOpen((data.features || []).length > 0);
    } catch {}
    finally { setLoading(false); }
  };

  const search = (text: string) => {
    setStreet(text);
    onChange(civicNum ? `${text} ${civicNum}` : text);
    if (timer.current) clearTimeout(timer.current);
    const {street: q} = splitStreetNumber(text);
    const query = q.length >= 3 ? q : text;
    if (query.length >= 3) timer.current = setTimeout(() => doFetch(query), 400);
    else { setSuggestions([]); setOpen(false); }
  };

  const handleBlur = () => {
    const {street: s, number: n} = splitStreetNumber(street);
    if (n && s.length >= 2) {
      setStreet(s);
      setCivicNum(n);
      onChange(n ? `${s} ${n}` : s);
    }
  };

  const select = (feature: any) => {
    const parsed     = parseFeature(feature);
    const streetName = parsed.address || featureLabel(feature);
    const {number: typedNum} = splitStreetNumber(street);
    const finalCivic = typedNum || civicNum;
    if (typedNum) setCivicNum(typedNum);
    setStreet(streetName);
    onChange(finalCivic ? `${streetName} ${finalCivic}` : streetName);
    onSelect?.({...parsed, address: streetName});
    setSuggestions([]);
    setOpen(false);
  };

  const onCivicChange = (n: string) => {
    setCivicNum(n);
    onChange(n ? `${street} ${n}` : street);
  };

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div ref={wrapRef} className={className}>
      {label && <label className="form-label">{label}</label>}
      <div style={{display: 'flex', gap: 8, alignItems: 'flex-start'}}>
        {/* Street autocomplete */}
        <div style={{flex: 1, position: 'relative'}}>
          <div style={{position: 'relative'}}>
            <input
              className="form-input"
              value={street}
              onChange={e => search(e.target.value)}
              onBlur={handleBlur}
              placeholder={placeholder || 'Via Roma'}
              autoComplete="off"
            />
            {loading && (
              <span className="mi mi-18 spin" style={{position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--primary)'}}>
                sync
              </span>
            )}
          </div>
          {open && suggestions.length > 0 && (
            <div style={{
              position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
              background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: 10, zIndex: 100,
              boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
              overflow: 'hidden',
            }}>
              {suggestions.map((feat, i) => (
                <button
                  key={i}
                  type="button"
                  onMouseDown={() => select(feat)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    width: '100%', padding: '10px 14px',
                    background: 'none', border: 'none', cursor: 'pointer',
                    textAlign: 'left', color: 'var(--text-1)', fontSize: 13,
                    borderBottom: i < suggestions.length - 1 ? '1px solid var(--border)' : 'none',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'var(--s2)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                >
                  <span className="mi mi-16" style={{color: 'var(--text-3)', flexShrink: 0}}>place</span>
                  <span style={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                    {featureLabel(feat)}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Civic number */}
        <input
          className="form-input"
          style={{width: 72, textAlign: 'center', flexShrink: 0}}
          value={civicNum}
          onChange={e => onCivicChange(e.target.value)}
          placeholder="N°"
          autoComplete="off"
          maxLength={6}
        />
      </div>
    </div>
  );
}
